// aluno/consent-modal.js — Modal de aceite LGPD (1º login).
//
// Exibido logo após o Google OAuth bem-sucedido, antes de liberar o app.
// 6 finalidades granulares: 2 obrigatórias (pré-marcadas e travadas) e 4 opcionais.
// Requisitos LGPD (Art. 7º/8º):
//   - Consentimento específico, destacado, livre, informado e inequívoco
//   - Granularidade por finalidade (não pode ser consentimento em bloco)
//   - Revogável a qualquer momento (tela de perfil — H5)
//   - Registro com data, versão do termo e snapshot das finalidades aceitas
//
// Contrato público:
//   openConsentModal(opts) → Promise<{accepted, choices, version, timestamp} | null>
//     - Resolve com null se o usuário fechar sem aceitar (fluxo cancelado → logout)
//     - Resolve com o snapshot aprovado para o chamador persistir
//
// Decisões travadas:
//   - Versão do termo: TERMO_VERSION (incrementa quando alterarmos os textos)
//   - Finalidade 1 e 2 são obrigatórias (execução do curso + registro pedagógico)
//   - Finalidade 3 (marketing), 4 (análise de uso — GA4), 5 (imagem/voz) e 6
//     (depoimentos) são opcionais
//   - Persistência local em sessionStorage (chave eloquence_consent_v1) + backend
//     H4: backend recebe via api.registrarConsentimento (append-only na aba
//     CONSENTIMENTOS_LGPD). Best-effort: se a rede falhar, mantém local e segue.
//   - Ao aceitar (ou revogar), emite CustomEvent 'eloquence:consent-changed' no
//     window, com o snapshot em detail. Usado pelo módulo analytics.js para
//     atualizar o Google Consent Mode v2.

import { api } from '../core/api.js';
import { auth } from '../core/auth.js';
import { trackLgpdAcceptAll } from '../core/analytics.js';

const TERMO_VERSION = '1.1';
const CONSENT_STORAGE_KEY = 'eloquence_consent_v1';

const FINALIDADES = [
  {
    id: 'execucao_curso',
    titulo: 'Execução do curso',
    descricao: 'Matrícula, acesso ao app, registro de presença, emissão de certificado e comunicação sobre a turma.',
    base: 'Execução de contrato (Art. 7º, V LGPD)',
    obrigatoria: true,
  },
  {
    id: 'registro_pedagogico',
    titulo: 'Registro pedagógico',
    descricao: 'Registrar sua evolução, desempenho, feedbacks do instrutor e histórico de exercícios para acompanhamento personalizado.',
    base: 'Execução de contrato (Art. 7º, V LGPD)',
    obrigatoria: true,
  },
  {
    id: 'marketing',
    titulo: 'Comunicações da Eloquence',
    descricao: 'Marque para receber avisos sobre novos cursos, turmas, eventos gratuitos ou conteúdos exclusivos. Inclui anúncios personalizados em Google/YouTube (remarketing) — só para quem já visitou o app.',
    base: 'Consentimento (Art. 7º, I LGPD)',
    obrigatoria: false,
  },
  {
    id: 'analise_uso',
    titulo: 'Análise de uso do app',
    descricao: 'Marque para nos ajudar a entender como o app é usado (páginas mais acessadas, tempo de sessão, erros). Se não marcar, a gente ainda vê números básicos sem identificar você. Usamos Google Analytics 4.',
    base: 'Consentimento (Art. 7º, I LGPD)',
    obrigatoria: false,
  },
  {
    id: 'imagem_voz',
    titulo: 'Uso de imagem e voz em material didático',
    descricao: 'Trechos de gravações de aulas e exercícios usados como material pedagógico em turmas futuras, com identificação ou anonimizados.',
    base: 'Consentimento (Art. 7º, I + Art. 8º LGPD)',
    obrigatoria: false,
  },
  {
    id: 'depoimentos',
    titulo: 'Depoimentos e cases de sucesso',
    descricao: 'Autorizar que seu nome, foto e depoimento sejam usados em materiais de divulgação da Eloquence (site, redes sociais).',
    base: 'Consentimento (Art. 7º, I LGPD)',
    obrigatoria: false,
  },
];

// ── Estado do modal (apenas uma instância viva por vez) ──────────────────────

let _resolve = null;
let _overlayRef = null;

// ── API pública ──────────────────────────────────────────────────────────────

/**
 * Abre o modal de consentimento. Bloqueia até usuário decidir.
 *
 * @param {Object} opts
 * @param {string} opts.nomeUsuario - Primeiro nome para saudação (opcional)
 * @returns {Promise<ConsentSnapshot|null>}
 *
 * @typedef {Object} ConsentSnapshot
 * @property {true} accepted
 * @property {Record<string, boolean>} choices
 * @property {string} version
 * @property {number} timestamp
 */
export function openConsentModal(opts = {}) {
  if (_overlayRef) return Promise.resolve(null);

  return new Promise((resolve) => {
    _resolve = resolve;
    _render(opts.nomeUsuario || '');
  });
}

export function loadConsentSnapshot() {
  try {
    const raw = sessionStorage.getItem(CONSENT_STORAGE_KEY);
    if (!raw) return null;
    const snap = JSON.parse(raw);
    if (!snap?.version || !snap?.choices) return null;
    if (snap.version !== TERMO_VERSION) return null;
    return snap;
  } catch {
    return null;
  }
}

export function clearConsentSnapshot() {
  try { sessionStorage.removeItem(CONSENT_STORAGE_KEY); } catch {}
}

export function saveConsentSnapshot(choices) {
  const snap = {
    accepted: true,
    choices: { ...choices },
    version: TERMO_VERSION,
    timestamp: Date.now(),
  };
  try {
    sessionStorage.setItem(CONSENT_STORAGE_KEY, JSON.stringify(snap));
  } catch (e) {
    console.warn('[consent-modal] falha persistindo snapshot local:', e);
  }
  _emitConsentChanged(snap);
  return snap;
}

export function getFinalidades() {
  return FINALIDADES.map((f) => ({ ...f }));
}

export function getConsentTermoVersion() {
  return TERMO_VERSION;
}

// ── Evento global para o módulo analytics.js escutar ────────────────────────

function _emitConsentChanged(snapshot) {
  try {
    window.dispatchEvent(new CustomEvent('eloquence:consent-changed', {
      detail: snapshot,
    }));
  } catch (e) {
    // silencioso — ambientes muito antigos sem CustomEvent
  }
}

// ── Render ───────────────────────────────────────────────────────────────────

function _render(primeiroNome) {
  const saudacao = primeiroNome ? `Olá, ${_escapeHtml(primeiroNome)}` : 'Olá';

  const overlay = document.createElement('div');
  overlay.className = 'consent-overlay';
  overlay.setAttribute('role', 'dialog');
  overlay.setAttribute('aria-modal', 'true');
  overlay.setAttribute('aria-labelledby', 'consent-title');

  overlay.innerHTML = `
    <div class="consent-card" role="document">
      <header class="consent-header">
        <h1 id="consent-title" class="consent-title">${saudacao}. Antes de começar.</h1>
        <p class="consent-subtitle">
          Para usar o App Eloquence, precisamos da sua autorização sobre como tratamos seus dados pessoais,
          conforme a <strong>Lei Geral de Proteção de Dados (LGPD)</strong>.
        </p>
      </header>

      <div class="consent-body">
        <p class="consent-intro">
          As duas primeiras autorizações são necessárias para o funcionamento do curso.
          As outras quatro são opcionais — você pode revogar qualquer uma a qualquer momento.
        </p>

        <div class="consent-quick-actions">
          <button type="button" class="consent-btn-accept-all" data-consent-action="aceitar-todos">
            Aceitar todas as autorizações opcionais
          </button>
          <button type="button" class="consent-btn-choose" data-consent-action="escolher">
            Quero escolher item a item
          </button>
        </div>

        <ul class="consent-list" role="list">
          ${FINALIDADES.map(_renderFinalidade).join('')}
        </ul>

        <p class="consent-links">
          <a href="#/termo" class="consent-link" data-consent-link="termo">Ler Termo de Consentimento completo</a>
          <span aria-hidden="true"> · </span>
          <a href="#/privacidade" class="consent-link" data-consent-link="privacidade">Ler Política de Privacidade</a>
        </p>
      </div>

      <footer class="consent-footer">
        <button type="button" class="consent-btn-secondary" data-consent-action="recusar">
          Não aceitar e sair
        </button>
        <button type="button" class="consent-btn-primary" data-consent-action="aceitar" disabled>
          Li e aceito
        </button>
      </footer>
    </div>
  `;

  document.body.appendChild(overlay);
  document.body.classList.add('consent-no-scroll');
  _overlayRef = overlay;

  _bindEvents(overlay);
  _updateAcceptButton(overlay);
}

function _renderFinalidade(f, idx) {
  const checkedAttr = f.obrigatoria ? 'checked disabled' : '';
  const obrigBadge = f.obrigatoria
    ? '<span class="consent-badge consent-badge-required">Obrigatório</span>'
    : '<span class="consent-badge consent-badge-optional">Opcional</span>';

  return `
    <li class="consent-item" data-obrigatoria="${f.obrigatoria}">
      <label class="consent-label" for="consent-${f.id}">
        <input
          type="checkbox"
          id="consent-${f.id}"
          class="consent-checkbox"
          data-finalidade="${f.id}"
          data-obrigatoria="${f.obrigatoria}"
          ${checkedAttr}
        />
        <div class="consent-item-content">
          <div class="consent-item-header">
            <span class="consent-item-number">${idx + 1}</span>
            <span class="consent-item-titulo">${_escapeHtml(f.titulo)}</span>
            ${obrigBadge}
          </div>
          <p class="consent-item-descricao">${_escapeHtml(f.descricao)}</p>
          <p class="consent-item-base">${_escapeHtml(f.base)}</p>
        </div>
      </label>
    </li>
  `;
}

// ── Eventos ──────────────────────────────────────────────────────────────────

function _bindEvents(overlay) {
  overlay.querySelectorAll('.consent-checkbox').forEach((cb) => {
    cb.addEventListener('change', () => _updateAcceptButton(overlay));
  });

  overlay.querySelector('[data-consent-action="aceitar"]')
    ?.addEventListener('click', () => _onAceitar(overlay));
  overlay.querySelector('[data-consent-action="recusar"]')
    ?.addEventListener('click', () => _onRecusar());
  overlay.querySelector('[data-consent-action="aceitar-todos"]')
    ?.addEventListener('click', () => _onAceitarTodos(overlay));
  overlay.querySelector('[data-consent-action="escolher"]')
    ?.addEventListener('click', () => _onEscolher(overlay));

  overlay.addEventListener('keydown', (e) => {
    if (e.key === 'Escape') e.preventDefault();
  });

  overlay.querySelectorAll('[data-consent-link]').forEach((a) => {
    a.addEventListener('click', (e) => {
      const rota = a.getAttribute('data-consent-link');
      window.open(`/#/${rota}`, '_blank', 'noopener');
      e.preventDefault();
    });
  });
}

function _onAceitarTodos(overlay) {
  overlay.querySelectorAll('.consent-checkbox[data-obrigatoria="false"]')
    .forEach((cb) => { cb.checked = true; });
  _updateAcceptButton(overlay);
  // Analytics: usuário optou por aceitar todas de uma vez (atalho UX).
  // Não persiste o snapshot — isso só acontece ao clicar em "Aceitar".
  trackLgpdAcceptAll();
  overlay.querySelector('[data-consent-action="aceitar"]')
    ?.scrollIntoView({ behavior: 'smooth', block: 'center' });
}

function _onEscolher(overlay) {
  overlay.querySelector('.consent-list')
    ?.scrollIntoView({ behavior: 'smooth', block: 'start' });
}

function _updateAcceptButton(overlay) {
  const todasObrigatoriasMarcadas = Array.from(
    overlay.querySelectorAll('.consent-checkbox[data-obrigatoria="true"]')
  ).every((cb) => cb.checked);

  const btn = overlay.querySelector('[data-consent-action="aceitar"]');
  if (btn) btn.disabled = !todasObrigatoriasMarcadas;
}

// ── Ações ────────────────────────────────────────────────────────────────────

function _onAceitar(overlay) {
  const choices = {};
  overlay.querySelectorAll('.consent-checkbox').forEach((cb) => {
    choices[cb.dataset.finalidade] = cb.checked;
  });

  const snapshot = {
    accepted: true,
    choices,
    version: TERMO_VERSION,
    timestamp: Date.now(),
  };

  try {
    sessionStorage.setItem(CONSENT_STORAGE_KEY, JSON.stringify(snapshot));
  } catch (e) {
    console.warn('[consent-modal] falha persistindo snapshot local:', e);
  }

  _persistirBackend(snapshot);
  _emitConsentChanged(snapshot);

  _close();
  if (_resolve) {
    const r = _resolve;
    _resolve = null;
    r(snapshot);
  }
}

async function _persistirBackend(snapshot) {
  const current = auth.getCurrent();
  const idToken = current?.idToken;
  if (!idToken) {
    console.warn('[consent-modal] sem idToken, pulando sync com backend');
    return;
  }
  const result = await api.registrarConsentimento({
    idToken,
    version: snapshot.version,
    choices: snapshot.choices,
    uaHash: _uaHashCurto(),
  });
  if (!result.ok) {
    console.warn('[consent-modal] backend falhou, snapshot só local:', result.error);
  }
}

function _uaHashCurto() {
  const ua = (navigator?.userAgent || '').slice(0, 200);
  let h = 0;
  for (let i = 0; i < ua.length; i++) {
    h = ((h << 5) - h + ua.charCodeAt(i)) | 0;
  }
  return 'ua_' + (h >>> 0).toString(36);
}

function _onRecusar() {
  _close();
  if (_resolve) {
    const r = _resolve;
    _resolve = null;
    r(null);
  }
}

function _close() {
  if (_overlayRef && _overlayRef.parentNode) {
    _overlayRef.parentNode.removeChild(_overlayRef);
  }
  _overlayRef = null;
  document.body.classList.remove('consent-no-scroll');
}

// ── Utils ────────────────────────────────────────────────────────────────────

function _escapeHtml(s) {
  return String(s ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#039;');
}
